import { useState, useEffect, useRef } from 'react';
import { OKX, UNISAT, XVERSE } from '@omnisat/lasereyes-core';
import { useLaserEyes, WalletIcon } from '@omnisat/lasereyes-react';
import { C, F, Ey, Tag, Spin, SCRIM, trans } from './mobUI';
import { useMobT } from './mobI18n';
import { useToast } from '../components/Toast';
import { classifyWalletError } from '../utils/walletError';
import { useScrollLock } from '../hooks/useScrollLock';
import { UnisatGlyph } from '../config/design';
import { MobReadMe } from './MobReadMe';

// Universal links, not custom schemes (`okx://`, `xverse://`): a custom scheme with
// the app missing makes Safari show "address is invalid" — a universal link opens the
// app when installed and falls through to the wallet's download page when not.
function deepLink(key) {
  const url = typeof window !== 'undefined' ? encodeURIComponent(window.location.href) : '';
  if (key === 'xverse') return `https://connect.xverse.app/browser?url=${url}`;
  if (key === 'okx') return `https://web3.okx.com/download?deeplink=${encodeURIComponent(`okx://wallet/dapp/url?dappUrl=${url}`)}`;
  // Unisat has no reliable browser deep-link scheme - fall back to the install page
  // rather than firing a bare `unisat://` that silently does nothing.
  return null;
}
// Mobile intentionally omits OYL - it's a desktop/browser extension with no mobile
// app deep-link (see ConnectWalletModal.js for the desktop list).
// Glyphs are the official wallet logos shipped by LaserEyes (WalletIcon), except
// Unisat which is inlined (UnisatGlyph) to avoid LaserEyes' shared-SVG-id collision.
const WALLETS = [
  { key:'okx',    name:'OKX',    constant:OKX,    glyph:<WalletIcon size={34} walletName={OKX}/>,    installUrl:'https://web3.okx.com/' },
  { key:'xverse', name:'Xverse', constant:XVERSE, glyph:<WalletIcon size={34} walletName={XVERSE}/>, installUrl:'https://www.xverse.app/download' },
  { key:'unisat', name:'Unisat', constant:UNISAT, glyph:<UnisatGlyph size={34}/>,                    installUrl:'https://unisat.io/download' },
];
const serif = (size, color) => ({ fontFamily:F.body, fontWeight:800, fontSize:size, letterSpacing:-.2, color, lineHeight:1.15 });

export function MobConnectModal({ open, onClose, onConnect }) {
  const { t } = useMobT();
  const { hasUnisat, hasXverse, hasOkx, connected } = useLaserEyes();
  // reactive LaserEyes flags + window.* fallback (matches desktop), so a wallet
  // LaserEyes already sees isn't missed before the global injects
  const detected = {
    unisat: hasUnisat || (typeof window !== 'undefined' && !!window.unisat),
    xverse: hasXverse || (typeof window !== 'undefined' && !!window.XverseProviders),
    okx:    hasOkx    || (typeof window !== 'undefined' && !!window.okxwallet),
  };
  const { showToast } = useToast();
  const [connecting, setConnecting] = useState(null);
  const attemptRef = useRef(0);   // per-attempt id so the watchdog and pick()'s catch never both settle one attempt
  const [readme, setReadme] = useState(false);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (open) { setShown(false); setConnecting(null);
      const id = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      return () => cancelAnimationFrame(id);
    }
  }, [open]);

  useScrollLock(open);   // pin the background (iOS-safe, ref-counted) while the sheet is open

  // Watchdog for a connect() that never settles. Xverse on iOS opens the wallet
  // via a universal link; if the user taps "Cancel" on the native "Open in
  // Xverse?" prompt the wallet promise neither resolves nor rejects, so pick()'s
  // catch never fires and the row spins forever. If the attempt hasn't settled
  // after a grace period of FOREGROUND time, treat it as cancelled: clear the
  // spinner and surface the rejection toast. The countdown only advances while
  // the page is visible, so a real approval (which backgrounds the page into the
  // wallet app) is never cut short.
  // Only the universal-link wallets (Xverse / OKX on iOS) can leave connect() pending
  // forever when the user cancels the native "Open in <app>?" prompt. In-page wallets
  // (Unisat in its in-app browser) always settle and their approval keeps the page
  // visible, so watching them would false-trip the rejection on a slow-but-valid approve.
  useEffect(() => {
    if (!open || !connecting || connected || (connecting !== 'xverse' && connecting !== 'okx')) return;
    let fg = 0;
    const STEP = 1000, LIMIT = 12000;
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      fg += STEP;
      if (fg >= LIMIT) {
        clearInterval(id);
        attemptRef.current++;   // supersede the in-flight pick() so its later catch is a no-op (no dup toast / state race)
        setConnecting(null);
        showToast(t('mc_errRejected'), { type: 'error' });
      }
    }, STEP);
    return () => clearInterval(id);
  }, [open, connecting, connected, showToast, t]);

  if (!open) return null;

  const anyInstalled = WALLETS.some(w => detected[w.key]);
  const pick = async (w) => {
    if (!detected[w.key] || connecting) return;
    const myAttempt = ++attemptRef.current;
    setConnecting(w.key);
    try { await onConnect(w.constant); }   // parent closes the sheet once `connected` flips
    catch (e) {
      if (attemptRef.current !== myAttempt) return;   // already settled by the watchdog / superseded
      showToast(classifyWalletError(e) === 'rejected' ? t('mc_errRejected') : t('mc_connectFailed'), { type: 'error' });
      setConnecting(null);
    }
  };
  const sheetTranslate = shown ? '0' : '100%';
  const backdropOp = shown ? 1 : 0;

  return (
    <div style={{position:'fixed',inset:0,zIndex:300}}>
      <div onClick={connecting?undefined:onClose} style={{position:'absolute',inset:0,background:SCRIM,opacity:backdropOp,transition:trans('opacity .3s')}}/>
      <div style={{position:'absolute',left:0,right:0,bottom:0,background:C.card,touchAction:'none',overscrollBehavior:'contain',paddingTop:10,
        borderRadius:'20px 20px 0 0',boxShadow:`0 -1px 0 ${C.rule}`,paddingBottom:'max(26px,env(safe-area-inset-bottom,26px))',
        transform:`translateY(${sheetTranslate})`,transition:trans('transform .34s cubic-bezier(.2,.9,.3,1)')}}>
        <div>
          <div style={{padding:'10px 20px 14px'}}>
            <Ey>{t('mc_selectWallet')}</Ey>
            <div style={{marginTop:6}}><span style={serif(22,C.ink)}>{t('mc_connectFloor')} {t('mc_connectHi')}</span></div>
          </div>
        </div>
        {WALLETS.map((w)=>{
          const installed = detected[w.key];
          const isC = connecting === w.key;
          const actionable = installed && !connecting;
          return (
            <div key={w.key} role="button" aria-label={w.name} aria-disabled={!actionable} tabIndex={actionable?0:-1}
              onClick={()=>actionable && pick(w)}
              onKeyDown={e=>{ if((e.key==='Enter'||e.key===' ')&&actionable){ e.preventDefault(); pick(w); } }}
              style={{margin:'0 16px 6px',padding:'12px 14px',borderRadius:16,background:C.sunk,display:'flex',alignItems:'center',gap:13,
                cursor:actionable?'pointer':'default',opacity:connecting&&!isC?.4:1,transition:'opacity .15s',outline:'none'}}>
              {w.glyph}
              <span style={serif(17,installed?C.ink:C.dim)}>{w.name}</span>
              <div style={{marginLeft:'auto'}}>
                {isC
                  ? <Spin size={18}/>
                  : installed
                    ? <Tag kind="ok">{t('mc_installed')}</Tag>
                    : <a href={deepLink(w.key)||w.installUrl} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()} style={{textDecoration:'none'}}><Tag kind="line">{t('mc_openApp')}</Tag></a>}
              </div>
            </div>
          );
        })}
        {!anyInstalled && (
          <div style={{margin:'4px 16px 0',padding:'12px 14px',borderRadius:16,background:C.sunk}}>
            <span style={{fontFamily:F.body,fontSize:12.5,fontWeight:500,color:C.dim,lineHeight:1.5}}>{t('mc_openInApp')}</span>
          </div>
        )}
        <div style={{padding:'14px 20px 0'}}>
          <span style={{fontFamily:F.body,fontSize:12,fontWeight:500,color:C.faint}}>{t('mc_termsPre')}<span onClick={()=>setReadme(true)} style={{color:C.ink,fontWeight:800,textDecoration:'underline',textUnderlineOffset:2,cursor:'pointer'}}>{t('mc_termsLink')}</span>.</span>
        </div>
      </div>
      <MobReadMe open={readme} onClose={()=>setReadme(false)}/>
    </div>
  );
}
